import { useEffect, useRef } from 'react';
import type { DriveItem } from '@/mocks/driveFiles';

interface ContextMenuProps {
  x: number;
  y: number;
  item: DriveItem;
  isTrashView: boolean;
  onClose: () => void;
  onDownload: (item: DriveItem) => void;
  onDelete: (item: DriveItem) => void;
  onRename: (item: DriveItem) => void;
  onOpenFolder: (folderId: string) => void;
  onRestore: (item: DriveItem) => void;
  onPermanentDelete: (item: DriveItem) => void;
}

export default function ContextMenu({
  x,
  y,
  item,
  isTrashView,
  onClose,
  onDownload,
  onDelete,
  onRename,
  onOpenFolder,
  onRestore,
  onPermanentDelete,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const adjustedX = Math.min(x, window.innerWidth - 200);
  const adjustedY = Math.min(y, window.innerHeight - 280);

  const isFolder = item.type === 'folder';

  const normalMenuItems = [
    ...(isFolder
      ? [
          {
            icon: 'ri-folder-open-line',
            label: '열기',
            action: () => onOpenFolder(item.id),
            color: 'text-foreground-700',
          },
        ]
      : []),
    {
      icon: 'ri-download-2-line',
      label: '다운로드',
      action: () => onDownload(item),
      color: 'text-foreground-700',
    },
    {
      icon: 'ri-edit-line',
      label: '이름 바꾸기',
      action: () => onRename(item),
      color: 'text-foreground-700',
    },
    { type: 'divider' as const },
    {
      icon: 'ri-delete-bin-line',
      label: '휴지통으로 이동',
      action: () => onDelete(item),
      color: 'text-accent-600',
      danger: true,
    },
  ];

  const trashMenuItems = [
    {
      icon: 'ri-arrow-go-back-line',
      label: '복구',
      action: () => onRestore(item),
      color: 'text-primary-600',
    },
    { type: 'divider' as const },
    {
      icon: 'ri-delete-bin-6-line',
      label: '영구 삭제',
      action: () => onPermanentDelete(item),
      color: 'text-accent-600',
      danger: true,
    },
  ];

  const menuItems = isTrashView ? trashMenuItems : normalMenuItems;

  return (
    <div
      ref={menuRef}
      className="fixed z-[100] bg-background-50 border border-background-200 rounded-xl py-1.5 min-w-[180px] overflow-hidden"
      style={{ left: `${adjustedX}px`, top: `${adjustedY}px` }}
    >
      <div className="px-3 py-2 border-b border-background-100">
        <p className="text-xs font-semibold text-foreground-500 truncate">{item.name}</p>
        <p className="text-xs text-foreground-400 mt-0.5">
          {isFolder ? '폴더' : item.mimeType ?? '파일'}
        </p>
      </div>
      {menuItems.map((menuItem, index) => {
        if ('type' in menuItem && menuItem.type === 'divider') {
          return <div key={index} className="my-1 border-t border-background-100"></div>;
        }
        if ('action' in menuItem) {
          return (
            <button
              key={index}
              onClick={() => {
                menuItem.action();
                onClose();
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                menuItem.danger
                  ? 'text-accent-600 hover:bg-accent-50'
                  : `${menuItem.color} hover:bg-background-100`
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={menuItem.icon}></i>
              </span>
              <span>{menuItem.label}</span>
            </button>
          );
        }
        return null;
      })}
    </div>
  );
}